// Cross-app SSO (BBD-50). A user signed in here can be signed into another Bluebird Suite
// app without re-entering credentials: this module issues a short-lived code on demand
// (for outbound links to append) and redeems an inbound code on load. Codes are issued
// on demand — warmSSOCode() on menu-open, not on a timer — because the old 30s refresh
// loop kept the serverless database awake around the clock.
import { auth } from '../firebase';
import { signInWithCustomToken } from 'firebase/auth';

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';

let ssoState = { code: null, expiresAt: 0 };
let inflight = null;

// Redeem a ?sso=<code> param left by another Bluebird Suite app. Single-use, so the param is
// stripped from the URL immediately, before the network call resolves.
export const trySSOSignIn = async () => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('sso');
        if (!code) return false;

        params.delete('sso');
        const newSearch = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash);

        try {
                const res = await fetch(`${API_BASE}/users/sso/redeem/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ code }),
                });
                if (!res.ok) return false;
                const { custom_token } = await res.json();
                await signInWithCustomToken(auth, custom_token);
                return true;
        } catch (err) {
                console.error('SSO sign-in failed:', err);
                return false;
        }
};

const fetchSSOCode = async () => {
        try {
                const user = auth.currentUser;
                if (!user) return;
                const idToken = await user.getIdToken();
                const res = await fetch(`${API_BASE}/users/sso/issue/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': idToken },
                });
                if (!res.ok) return;
                const { code, expires_in } = await res.json();
                ssoState = { code, expiresAt: Date.now() + expires_in * 1000 - 5000 };
        } catch {
                // Best-effort — the outbound link just won't carry SSO this time.
        }
};

// Fetch a code just before it's needed — call when the user shows intent to leave (e.g.
// opening the suite menu). The 45s server-side TTL comfortably covers the click that
// follows. Resolves once a fresh code is cached (or immediately if one already is);
// concurrent calls share one request.
export const warmSSOCode = async () => {
        if (ssoState.code && Date.now() < ssoState.expiresAt) return;
        if (!inflight) inflight = fetchSSOCode().finally(() => { inflight = null; });
        return inflight;
};

// Drop any cached code (call on sign-out).
export const clearSSOCode = () => {
        ssoState = { code: null, expiresAt: 0 };
};

// Append the current SSO code to a URL for a link to another suite app. Returns the URL
// unchanged if no fresh code is available (logged out, or no warm has landed yet).
export const appendSSOCode = (url) => {
        if (!ssoState.code || Date.now() > ssoState.expiresAt) return url;
        const target = new URL(url);
        target.searchParams.set('sso', ssoState.code);
        return target.toString();
};
